import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { searchCentroid } from '../api';
import Header from '../components/Header';

interface SearchResult {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  distance: number;
  matchType: string;
}

interface SearchResponse {
  query: string;
  maxDistance: number;
  searchType: string;
  results: SearchResult[];
}

interface CentroidResult {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  score: number;
  accepted: boolean;
  matchType: string;
  crawled_at: string;
}

export default function CentroidPage() {
  const [query, setQuery] = useState('');
  const [maxDistance, setMaxDistance] = useState('0.5');
  const [searchType, setSearchType] = useState('content');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection for centroid
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Centroid results
  const [centroidResults, setCentroidResults] = useState<
    CentroidResult[] | null
  >(null);
  const [centroidLoading, setCentroidLoading] = useState(false);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setCentroidResults(null);
    try {
      const params = new URLSearchParams({
        q: query.trim(),
        maxDistance,
        searchType,
      });
      const res = await fetch(`/api/embedding-search?${params}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed: ${res.status}`);
      }
      const data: SearchResponse = await res.json();
      setResults(data.results);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (headlineId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(headlineId)) {
        next.delete(headlineId);
      } else {
        next.add(headlineId);
      }
      return next;
    });
  };

  const handleTryIt = async () => {
    if (selected.size === 0) return;
    setCentroidLoading(true);
    setError(null);
    try {
      const data = await searchCentroid([...selected], searchType);
      setCentroidResults(data.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Centroid search failed.');
    } finally {
      setCentroidLoading(false);
    }
  };

  return (
    <div className="page-search">
      <Header />
      <Link className="back-link" to="/">
        &larr; Back to headlines
      </Link>

      <h2>Topic Centroid Explorer</h2>

      <form onSubmit={handleSearch} className="search-form">
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Describe a topic (e.g. &quot;advances in nuclear fusion energy&quot;)..."
          className="search-input"
          rows={3}
        />
        <label className="distance-label">
          Max distance
          <input
            type="number"
            step="0.01"
            min="0"
            max="2"
            value={maxDistance}
            onChange={(e) => setMaxDistance(e.target.value)}
            className="distance-input"
          />
        </label>
        <label className="distance-label">
          Search in
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value)}
            className="search-type-select"
          >
            <option value="content">Content only</option>
            <option value="title">Title only</option>
            <option value="both">Title + Content</option>
          </select>
        </label>
        <button type="submit" disabled={loading || !query.trim()}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <p className="error">{error}</p>}

      {results !== null && (
        <div className="search-results">
          <p className="result-count">
            {results.length} results &mdash; select headlines to build a
            centroid
          </p>
          <table>
            <thead>
              <tr>
                <th />
                <th>#</th>
                <th>Dist</th>
                <th>Headline</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={`${r.headline_id}-${i}`}>
                  <td className="col-polarity">
                    <input
                      type="checkbox"
                      checked={selected.has(r.headline_id)}
                      onChange={() => toggleSelected(r.headline_id)}
                    />
                  </td>
                  <td className="col-rank">{i + 1}</td>
                  <td className="col-dist">{r.distance.toFixed(4)}</td>
                  <td>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title}
                    </a>
                  </td>
                  <td className="col-source">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected.size > 0 && (
        <div className="filter-create-actions">
          <p>
            {selected.size} headline{selected.size !== 1 ? 's' : ''} selected
          </p>
          <button
            type="button"
            className="save-btn"
            disabled={centroidLoading}
            onClick={handleTryIt}
          >
            {centroidLoading ? 'Computing...' : 'Try it'}
          </button>
        </div>
      )}

      {centroidResults !== null && (
        <div className="search-results">
          <h3>Centroid Results (last 72h)</h3>
          <p className="result-count">{centroidResults.length} results</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Score</th>
                <th>Headline</th>
                <th>Source</th>
                <th>Match</th>
              </tr>
            </thead>
            <tbody>
              {centroidResults.map((r, i) => (
                <tr
                  key={`${r.url}-${i}`}
                  className={r.accepted ? '' : 'near-miss'}
                >
                  <td className="col-rank">{i + 1}</td>
                  <td className="col-date">
                    {new Date(`${r.crawled_at}Z`).toLocaleString()}
                  </td>
                  <td className="col-dist">{r.score.toFixed(4)}</td>
                  <td>
                    <a href={r.url} target="_blank" rel="noreferrer">
                      {r.title}
                    </a>
                  </td>
                  <td className="col-source">{r.source}</td>
                  <td className="col-match">{r.matchType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
